import { useState } from "react";
import {
  isSingletonProjectType,
  projectTypeLabel,
} from "../../../../../constants/projectTypes";
import { wbsLabel } from "../utils/treeLabels";

/**
 * "Add / Modify / Delete WBS" dialogs for one plant's WBS list.
 *
 * Wraps the data layer's create/update/delete calls with the form state,
 * validation messages and saving flags each dialog needs.
 */
export const useWbsOperations = ({
  createWbs,
  updateWbs,
  deleteWbs,
  wbsByPlant,
  loadWbsForPlant,
}) => {
  // Plant a "+ Add WBS" click is pending for (null = dialog closed).
  const [addWbsTarget, setAddWbsTarget] = useState(null);
  const [wbsForm, setWbsForm] = useState({
    projectName: "",
    description: "",
    type: "",
    link: "",
  });
  // Type the dialog was opened pinned to ("" = free choice). Set when the add
  // button of a discipline folder was used.
  const [lockedWbsType, setLockedWbsType] = useState("");
  const [savingWbs, setSavingWbs] = useState(false);
  const [wbsError, setWbsError] = useState("");

  // Right-click on a WBS row: where the menu opens and what it acts on
  // (null = no menu open).
  const [wbsMenu, setWbsMenu] = useState(null);

  // { plant, wbs } being renamed / redescribed (null = dialog closed).
  const [editWbsTarget, setEditWbsTarget] = useState(null);
  const [editWbsForm, setEditWbsForm] = useState({
    projectName: "",
    description: "",
  });
  const [savingWbsEdit, setSavingWbsEdit] = useState(false);
  const [editWbsError, setEditWbsError] = useState("");

  // { plant, wbs } pending deletion (null = dialog closed).
  const [deleteWbsTarget, setDeleteWbsTarget] = useState(null);
  const [deletingWbs, setDeletingWbs] = useState(false);
  const [deleteWbsError, setDeleteWbsError] = useState("");

  // `presetType` comes from the "+" on a discipline folder: that button adds
  // another WBS of the folder's own type, so the type is filled in and locked
  // rather than asked for again. The plant-level button passes none and the
  // picker stays open.
  const openAddWbs = (plant, presetType = "") => {
    setAddWbsTarget(plant);
    // The type picker disables singleton types (Spec) by reading the plant's
    // WBS list out of wbsByPlant - only cached once the plant node has been
    // expanded. Fetch it here too, so a "+ Add WBS" opened from a plant that
    // was never expanded doesn't let the check silently pass.
    loadWbsForPlant(plant?.id);
    setWbsForm({
      projectName: "",
      description: "",
      type: presetType,
      link: "",
    });
    setLockedWbsType(presetType);
    setWbsError("");
  };

  // A one-per-plant type (Spec) the plant already holds. Checked against every
  // WBS in the plant, not just those the current search leaves visible. The
  // create endpoint doesn't enforce this, so the dialog is what does.
  const isSingletonTypeTaken = (plant, type) => {
    if (!isSingletonProjectType(type)) return false;
    const plantWbs = wbsByPlant[plant?.id];
    const wanted = String(type).trim().toLowerCase();
    return (
      Array.isArray(plantWbs) &&
      plantWbs.some(
        (wbs) =>
          String(wbs?.type ?? "")
            .trim()
            .toLowerCase() === wanted,
      )
    );
  };

  const handleCreateWbs = async () => {
    const projectName = wbsForm.projectName.trim();
    if (!projectName) {
      setWbsError("WBS code is required");
      return;
    }
    if (!wbsForm.type) {
      setWbsError("Type is required");
      return;
    }
    if (isSingletonTypeTaken(addWbsTarget, wbsForm.type)) {
      setWbsError(
        `This plant already has a ${projectTypeLabel(wbsForm.type)} WBS - only one is allowed.`,
      );
      return;
    }
    setSavingWbs(true);
    setWbsError("");
    try {
      await createWbs(addWbsTarget.id, {
        projectName,
        description: wbsForm.description.trim(),
        type: wbsForm.type,
        link: wbsForm.link.trim(),
      });
      setAddWbsTarget(null);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to create WBS";
      setWbsError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingWbs(false);
    }
  };

  // Right-click anywhere on a WBS row. Opens at the pointer rather than
  // anchored to an element, so the menu lands where the user clicked.
  // preventDefault keeps the browser's own menu away; stopPropagation keeps
  // the click from selecting the node underneath it.
  const openWbsMenu = (event, plant, wbs) => {
    event.preventDefault();
    event.stopPropagation();
    setWbsMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      plant,
      wbs,
    });
  };

  const closeWbsMenu = () => setWbsMenu(null);

  const openEditWbs = (plant, wbs) => {
    setEditWbsTarget({ plant, wbs });
    setEditWbsForm({
      projectName: wbsLabel(wbs),
      description: wbs?.description || "",
    });
    setEditWbsError("");
    closeWbsMenu();
  };

  const handleUpdateWbs = async () => {
    const projectName = editWbsForm.projectName.trim();
    if (!projectName) {
      setEditWbsError("WBS code is required");
      return;
    }

    setSavingWbsEdit(true);
    setEditWbsError("");
    try {
      await updateWbs(editWbsTarget.plant.id, editWbsTarget.wbs.id, {
        projectName,
        description: editWbsForm.description.trim(),
      });
      setEditWbsTarget(null);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to update WBS";
      setEditWbsError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSavingWbsEdit(false);
    }
  };

  const openDeleteWbs = (plant, wbs) => {
    setDeleteWbsTarget({ plant, wbs });
    setDeleteWbsError("");
    closeWbsMenu();
  };

  const handleDeleteWbs = async () => {
    setDeletingWbs(true);
    setDeleteWbsError("");
    try {
      await deleteWbs(deleteWbsTarget.plant.id, deleteWbsTarget.wbs.id);
      setDeleteWbsTarget(null);
    } catch (err) {
      // Most often the backend refusing to delete a WBS other rows still
      // point at (documents, drawings) - worth showing verbatim.
      const message = err?.response?.data?.message || "Failed to delete WBS";
      setDeleteWbsError(
        Array.isArray(message) ? message.join(", ") : message,
      );
    } finally {
      setDeletingWbs(false);
    }
  };

  return {
    addWbsTarget,
    setAddWbsTarget,
    wbsForm,
    setWbsForm,
    lockedWbsType,
    savingWbs,
    wbsError,
    openAddWbs,
    isSingletonTypeTaken,
    handleCreateWbs,
    wbsMenu,
    openWbsMenu,
    closeWbsMenu,
    editWbsTarget,
    setEditWbsTarget,
    editWbsForm,
    setEditWbsForm,
    savingWbsEdit,
    editWbsError,
    openEditWbs,
    handleUpdateWbs,
    deleteWbsTarget,
    setDeleteWbsTarget,
    deletingWbs,
    deleteWbsError,
    openDeleteWbs,
    handleDeleteWbs,
  };
};
